/**
 * Color themes (device-local). Ten agent-designed, WCAG-verified schemes
 * (five dark, five light); token values live as CSS variables in @draftedge/ui styles.css
 * (:root = midnight, .theme-* classes override). This module owns
 * selection, persistence, and application.
 */
export interface ThemeDef {
  key: string;
  name: string;
  blurb: string;
  /** Light ("white mode") theme - flips the iOS status bar to dark text. */
  light?: boolean;
  /** Swatch preview colors for the picker (hex - display only). */
  swatch: { surface: string; raised: string; accent: string };
}

export const THEMES: ThemeDef[] = [
  {
    key: 'midnight',
    name: 'Midnight',
    blurb: 'Pure black, electric green. The original.',
    swatch: { surface: '#0b0b0b', raised: '#141414', accent: '#1aff66' },
  },
  {
    key: 'reserve',
    name: 'Reserve',
    blurb: 'The DraftEdge deep navy, boardroom calm.',
    swatch: { surface: '#161c29', raised: '#1d2331', accent: '#1aff66' },
  },
  {
    key: 'ember',
    name: 'Ember',
    blurb: 'Warm espresso with a champagne accent.',
    swatch: { surface: '#120e0c', raised: '#1c1613', accent: '#e9cea0' },
  },
  {
    key: 'steel',
    name: 'Steel',
    blurb: 'Cool graphite, glacier-blue highlights.',
    swatch: { surface: '#0e1116', raised: '#161b22', accent: '#8fd8ff' },
  },
  {
    key: 'sovereign',
    name: 'Sovereign',
    blurb: 'Royal violet for prime-time draft nights.',
    swatch: { surface: '#100d17', raised: '#181322', accent: '#bda6ff' },
  },
  {
    key: 'paper',
    name: 'Paper',
    blurb: 'Gallery white with forest-green conviction.',
    light: true,
    swatch: { surface: '#f6f7f4', raised: '#ffffff', accent: '#0b6b4f' },
  },
  {
    key: 'ivory',
    name: 'Ivory',
    blurb: 'Warm cream broadsheet, burnt-sienna heat.',
    light: true,
    swatch: { surface: '#faf5eb', raised: '#fffdf7', accent: '#b03a12' },
  },
  {
    key: 'porcelain',
    name: 'Porcelain',
    blurb: 'Cool blue-white precision, cobalt lines.',
    light: true,
    swatch: { surface: '#f2f6fa', raised: '#ffffff', accent: '#1d5fd6' },
  },
  {
    key: 'lilac',
    name: 'Lilac',
    blurb: 'Soft lavender-white, royal violet accents.',
    light: true,
    swatch: { surface: '#f8f6fb', raised: '#fefdff', accent: '#6d3bd1' },
  },
  {
    key: 'gallery',
    name: 'Gallery',
    blurb: 'Editorial white, brand navy ink, brass CTAs.',
    light: true,
    swatch: { surface: '#fbfbfa', raised: '#ffffff', accent: '#a87812' },
  },
  {
    key: 'voltage',
    name: 'Voltage',
    blurb: 'Neon cyan arcs over indigo black.',
    swatch: { surface: '#0b0618', raised: '#171030', accent: '#00f0ff' },
  },
  {
    key: 'blitz',
    name: 'Blitz',
    blurb: 'Royal blue under electric-yellow floodlights.',
    swatch: { surface: '#08123a', raised: '#101f5c', accent: '#ffe600' },
  },
  {
    key: 'carnage',
    name: 'Carnage',
    blurb: 'Crimson strikes on smoked charcoal.',
    swatch: { surface: '#131013', raised: '#1f1a1c', accent: '#ff2442' },
  },
  {
    key: 'cathode',
    name: 'Cathode',
    blurb: 'Amber phosphor on pure CRT black.',
    swatch: { surface: '#000000', raised: '#120c02', accent: '#ffa028' },
  },
  {
    key: 'strike',
    name: 'Strike',
    blurb: 'Pure white, jet ink, one screaming magenta.',
    light: true,
    swatch: { surface: '#ffffff', raised: '#fafafa', accent: '#d6006f' },
  },
  {
    key: 'pylon',
    name: 'Pylon',
    blurb: 'Pine turf, blaze-orange pylon at the goal line.',
    swatch: { surface: '#06190e', raised: '#0d2b19', accent: '#ff7a1c' },
  },
  {
    key: 'hail-mary',
    name: 'Hail Mary',
    blurb: 'Twilight violet, one gold spiral in the lights.',
    swatch: { surface: '#16082e', raised: '#251148', accent: '#f6c445' },
  },
  {
    key: 'overtime',
    name: 'Overtime',
    blurb: 'Midnight aqua, sudden-death magenta neon.',
    swatch: { surface: '#03191f', raised: '#0a2e38', accent: '#ff3d9e' },
  },
  {
    key: 'throwback',
    name: 'Throwback',
    blurb: 'Wine-leather burgundy, icy chrome edge.',
    swatch: { surface: '#250810', raised: '#3a1220', accent: '#a5d8ff' },
  },
  {
    key: 'pigskin',
    name: 'Pigskin',
    blurb: 'Burnt-rust game ball, cream lace accents.',
    swatch: { surface: '#2a1104', raised: '#40200a', accent: '#ffe9c8' },
  },
  {
    key: 'venom',
    name: 'Venom',
    blurb: 'Oxblood menace split by acid chartreuse.',
    swatch: { surface: '#1a0714', raised: '#2a0d20', accent: '#c8ff2e' },
  },
  {
    key: 'kevlar',
    name: 'Kevlar',
    blurb: 'Matte olive fatigues, electric lavender.',
    swatch: { surface: '#14160c', raised: '#232719', accent: '#b98cff' },
  },
  {
    key: 'two-minute',
    name: '2-Minute',
    blurb: 'Ink-blue night, coral alarm.',
    swatch: { surface: '#060c1e', raised: '#0e1730', accent: '#ff6f4d' },
  },
  {
    key: 'foundry',
    name: 'Foundry',
    blurb: 'Wet cement and neon mint. Brutalist premium.',
    swatch: { surface: '#1a1815', raised: '#272420', accent: '#00efc0' },
  },
  {
    key: 'terrazzo',
    name: 'Terrazzo',
    blurb: 'Warm plaster gallery, deep petrol conviction.',
    light: true,
    swatch: { surface: '#efece5', raised: '#fbf9f4', accent: '#006d7a' },
  },
  {
    key: 'turf-midnight',
    name: 'Turf',
    blurb: 'Grass-tinged black lit by an ice-cyan scoreboard.',
    swatch: { surface: '#08100c', raised: '#0f1a14', accent: '#21e1f0' },
  },
  {
    key: 'vault-violet',
    name: 'Vault',
    blurb: 'Blackout draft room with an ultraviolet pulse.',
    swatch: { surface: '#0b0811', raised: '#16101f', accent: '#ee7bff' },
  },
  {
    key: 'ember-black',
    // Named Cinder, not Ember: the older `ember` theme already owns that
    // label, and two identically-named tiles in the picker are unpickable.
    // The key stays `ember-black` so anyone already on it keeps their theme.
    name: 'Cinder',
    blurb: 'Charcoal warm from the fire, laser-lime clock.',
    swatch: { surface: '#110a09', raised: '#1e1210', accent: '#b4f13f' },
  },
  {
    key: 'tidal-black',
    name: 'Tidal',
    blurb: 'Deep-water black under stadium ice.',
    swatch: { surface: '#060f0f', raised: '#0d1b1c', accent: '#55a9ff' },
  },
  {
    key: 'graphite-arc',
    name: 'Graphite',
    blurb: 'Warm gunmetal press box arced with violet.',
    swatch: { surface: '#120f0a', raised: '#1e1a13', accent: '#b98cff' },
  },
  {
    key: 'sideline',
    name: 'Sideline',
    blurb: 'Daylight page, header and footer in the logo navy.',
    light: true,
    swatch: { surface: '#f4f7fa', raised: '#ffffff', accent: '#0a2849' },
  },
  {
    key: 'endzone',
    name: 'Endzone',
    blurb: 'Daylight page, solid navy chrome matching the button.',
    light: true,
    swatch: { surface: '#e7edf5', raised: '#ffffff', accent: '#0a2849' },
  },
];

const THEME_KEY = 'de.theme';
/**
 * What a fresh install opens in (owner call).
 *
 * Deliberately NOT the same thing as ROOT_THEME below. Those were one
 * constant, which quietly meant "the default is whatever :root already
 * is" — so pointing the default at any other theme would have applied no
 * class at all and rendered Midnight while the picker claimed otherwise.
 */
const DEFAULT_THEME = 'tidal-black';

/**
 * The theme the bare :root variables ALREADY are. It needs no class; every
 * other theme is a class on <html>. This is a fact about the stylesheet,
 * not a preference.
 */
const ROOT_THEME = 'midnight';

export function getStoredTheme(): string {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v && THEMES.some((t) => t.key === v) ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Swap the .theme-* class on <html>. Midnight is :root, so it needs none. */
export function applyTheme(key: string): void {
  const el = document.documentElement;
  for (const t of THEMES) el.classList.remove(`theme-${t.key}`);
  const theme = THEMES.find((t) => t.key === key);
  if (key !== ROOT_THEME && theme) {
    el.classList.add(`theme-${key}`);
  }
  // Generic light/dark hook for the few spots CSS variables can't cover
  // (metal-tone tier chips, etc).
  el.classList.toggle('is-light', theme?.light === true);
  syncStatusBar(theme?.light === true);
}

/**
 * iOS status bar text must flip with the theme: light themes need dark
 * glyphs. Plugin access goes through the Capacitor global (present only in
 * the native shell); web is a no-op.
 */
function syncStatusBar(lightTheme: boolean): void {
  try {
    const cap = (
      window as unknown as {
        Capacitor?: {
          isNativePlatform?: () => boolean;
          Plugins?: {
            StatusBar?: {
              setStyle: (o: { style: string }) => Promise<void>;
            };
          };
        };
      }
    ).Capacitor;
    if (!cap?.isNativePlatform?.()) return;
    // Plugin Style semantics: LIGHT = light background (dark text).
    void cap.Plugins?.StatusBar?.setStyle({
      style: lightTheme ? 'LIGHT' : 'DARK',
    })?.catch(() => {});
  } catch {
    /* status bar stays as-is */
  }
}

export function setTheme(key: string): void {
  try {
    // Always stored, including the default. The old code deleted the key
    // for the default theme to keep storage tidy; that only worked while
    // the default WAS the class-less root, and an absent key must keep
    // meaning "never chose one" so the default can change again later.
    localStorage.setItem(THEME_KEY, key);
  } catch {
    /* selection just won't persist */
  }
  applyTheme(key);
}
